/**
 * StreakFree API Service
 * Handles all API calls to the FastAPI backend
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Token storage keys
const TOKEN_KEY = 'streakfree_token';
const USER_KEY = 'streakfree_user';

// Get stored token
export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Get stored user
export const getStoredUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

// Store auth data
const storeAuth = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// Clear auth data
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Check if authenticated
export const isAuthenticated = () => !!getToken();

// Helper to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      // Handle FastAPI error format
      if (errorData.detail) {
        errorMessage = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : JSON.stringify(errorData.detail);
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // JSON parsing failed, use status-based message
      if (response.status === 401) errorMessage = 'Invalid credentials';
      if (response.status === 400) errorMessage = 'Bad request';
      if (response.status === 422) errorMessage = 'Validation error';
    }
    
    // Clear auth on 401
    if (response.status === 401) {
      clearAuth();
    }
    throw new Error(errorMessage);
  }
  return response.json();
};

// Generic fetch wrapper with auth
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();
  
  // Merge headers properly - don't let options override auth
  const { headers: optionHeaders, ...restOptions } = options;
  
  const config = {
    ...restOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...optionHeaders,
    },
  };
  
  const response = await fetch(url, config);
  return handleResponse(response);
};

// ============================================
// AUTHENTICATION
// ============================================

export const register = async (email, password, name, companyName = null) => {
  const data = await apiFetch('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ 
      email, 
      password, 
      name,
      company_name: companyName 
    }),
  });
  storeAuth(data.access_token, data.user);
  // Dispatch event so ThemeContext can reload theme
  window.dispatchEvent(new Event('userLoggedIn'));
  return data;
};

export const login = async (email, password) => {
  const data = await apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  storeAuth(data.access_token, data.user);
  // Dispatch event so ThemeContext can reload theme
  window.dispatchEvent(new Event('userLoggedIn'));
  return data;
};

export const logout = async () => {
  // Try to logout Google session if exists
  try {
    await fetch(`${API_BASE_URL}/api/auth/google/logout`, {
      method: 'POST',
      credentials: 'include'
    });
  } catch (e) {
    // Ignore errors
  }
  clearAuth();
};

export const getCurrentUser = () => apiFetch('/api/auth/me');

// ============================================
// GOOGLE OAUTH
// ============================================

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const initiateGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const processGoogleSession = async (sessionId) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/google/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ session_id: sessionId }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Google login failed' }));
    throw new Error(error.detail || 'Google login failed');
  }
  
  const data = await response.json();
  // Store user for immediate access (cookie handles session)
  if (data.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    // Dispatch event so ThemeContext can reload theme
    window.dispatchEvent(new Event('userLoggedIn'));
  }
  return data;
};

export const getGoogleUser = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/google/me`, {
    credentials: 'include'
  });
  
  if (!response.ok) {
    throw new Error('Not authenticated');
  }
  
  return response.json();
};

export const changePassword = (currentPassword, newPassword) => 
  apiFetch('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ 
      current_password: currentPassword, 
      new_password: newPassword 
    }),
  });

// ============================================
// DASHBOARD
// ============================================

export const getDashboardStats = () => apiFetch('/api/dashboard/stats');

// ============================================
// CUSTOMERS
// ============================================

export const getCustomers = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/api/customers${query ? `?${query}` : ''}`);
};

export const getCustomer = (customerId) => apiFetch(`/api/customers/${customerId}`);

export const createCustomer = (customer) => apiFetch('/api/customers', {
  method: 'POST',
  body: JSON.stringify(customer),
});

export const updateCustomer = (customerId, updates) => apiFetch(`/api/customers/${customerId}`, {
  method: 'PUT',
  body: JSON.stringify(updates),
});

export const deleteCustomer = (customerId) => apiFetch(`/api/customers/${customerId}`, {
  method: 'DELETE',
});

// ============================================
// JOBS
// ============================================

export const getJobs = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/api/jobs${query ? `?${query}` : ''}`);
};

export const getJob = (jobId) => apiFetch(`/api/jobs/${jobId}`);

export const createJob = (job) => apiFetch('/api/jobs', {
  method: 'POST',
  body: JSON.stringify(job),
});

export const updateJob = (jobId, updates) => apiFetch(`/api/jobs/${jobId}`, {
  method: 'PUT',
  body: JSON.stringify(updates),
});

export const completeJob = (jobId, sendNotification = true, sendMethod = 'none', chargeAmount = null, paymentRoute = null, skipAutoCharge = false, workerLat = null, workerLng = null, occurrenceDate = null, frontOnly = false) => {
  const options = {
    method: 'POST',
  };
  
  // If any body-only field is set, send the whole payload as JSON.
  // workerLat/workerLng come from a best-effort GPS read on the device when
  // completing a job — used server-side to auto-correct stale customer pins.
  // occurrenceDate is the date the job appeared on in the Work Planner (the
  // date the worker actioned it on). Anchors the completed row to THAT day
  // so early-completions land on the right column of the planner.
  const useBody = chargeAmount !== null || paymentRoute !== null || skipAutoCharge || workerLat !== null || workerLng !== null || occurrenceDate !== null || frontOnly;
  if (useBody) {
    options.body = JSON.stringify({ 
      send_notification: sendNotification,
      send_method: sendMethod,
      charge_amount: chargeAmount,
      payment_route: paymentRoute,
      skip_auto_charge: skipAutoCharge,
      worker_lat: workerLat,
      worker_lng: workerLng,
      occurrence_date: occurrenceDate,
      front_only: frontOnly,
    });
  }
  
  // Use query params for backwards compatibility if no body
  const url = useBody
    ? `/api/jobs/${jobId}/complete`
    : `/api/jobs/${jobId}/complete?send_notification=${sendNotification}&send_method=${sendMethod}`;
    
  return apiFetch(url, options);
};

export const skipJob = (jobId, reason, occurrenceDate = null) => {
  const params = new URLSearchParams({ reason: reason || '' });
  if (occurrenceDate) params.set('occurrence_date', occurrenceDate);
  return apiFetch(`/api/jobs/${jobId}/skip?${params.toString()}`, {
    method: 'POST',
  });
};

export const rescheduleJob = (jobId, newDate, updateRecurring = false) => 
  apiFetch(`/api/jobs/${jobId}/reschedule?new_date=${newDate}&update_recurring=${updateRecurring}`, {
    method: 'POST',
  });

// ============================================
// INVOICES
// ============================================

export const getInvoices = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/api/invoices${query ? `?${query}` : ''}`);
};

export const getInvoice = (invoiceId) => apiFetch(`/api/invoices/${invoiceId}`);

export const markInvoicePaid = (invoiceId, paymentMethod = 'bacs') => 
  apiFetch(`/api/invoices/${invoiceId}/mark-paid?payment_method=${paymentMethod}`, {
    method: 'POST',
  });

// ============================================
// MESSAGES
// ============================================

export const getMessages = (customerId, params = {}) => {
  const query = new URLSearchParams({ customer_id: customerId, ...params }).toString();
  return apiFetch(`/api/messages?${query}`);
};

export const sendMessage = (message) => apiFetch('/api/messages', {
  method: 'POST',
  body: JSON.stringify(message),
});

// ============================================
// USERS/WORKERS
// ============================================

export const getUsers = () => apiFetch('/api/users');

export const createUser = (user) => apiFetch('/api/users', {
  method: 'POST',
  body: JSON.stringify(user),
});

// ============================================
// OWINGS
// ============================================

export const getOwings = () => apiFetch('/api/owings');

// ============================================
// UTILITIES
// ============================================

export const healthCheck = () => apiFetch('/api/health');

export const seedDatabase = () => apiFetch('/api/seed', { method: 'POST' });

// Export all as default object for convenience
export default {
  // Dashboard
  getDashboardStats,
  // Customers
  getCustomers,
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  // Jobs
  getJobs,
  getJob,
  createJob,
  updateJob,
  completeJob,
  skipJob,
  rescheduleJob,
  // Invoices
  getInvoices,
  getInvoice,
  markInvoicePaid,
  // Messages
  getMessages,
  sendMessage,
  // Users
  getUsers,
  createUser,
  // Owings
  getOwings,
  // Utilities
  healthCheck,
  seedDatabase,
  // Auth
  register,
  login,
  logout,
  getCurrentUser,
  changePassword,
  isAuthenticated,
  getToken,
  getStoredUser,
  clearAuth,
  // Google OAuth
  initiateGoogleLogin,
  processGoogleSession,
  getGoogleUser,
};
